import { defineStore } from 'pinia';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import {
  webOrdersAPI,
  webProductsAPI,
  webCustomersAPI,
  webOrderCustomersAPI,
  woocommerceStoresAPI,
  deliveryPartnerAccountsAPI,
  deliveryShipmentsAPI,
  webOrderSettingsAPI,
  webOrderPickupAddressesAPI,
  inventoryStockAPI,
  inventoryLogsAPI,
  inventoryVendorsAPI,
  inventoryWarehousesAPI,
  inventoryPurchasesAPI,
  inventoryBillsAPI,
} from 'dashboard/api/webOrder';
import { agentAvatarUrl } from 'dashboard/helper/agentAvatar';
import {
  buildFilterPayload,
  defaultOrderFilters,
  hasActiveOrderFilters,
  DELIVERY_TYPES,
  ORDER_SOURCES,
  ORDER_STATUSES,
  formatDateRangeLabel,
} from 'dashboard/routes/dashboard/webOrder/helpers/orderFilters';

/** Map API/camelCase status keys back to UI kebab keys (ready-to-go, on-hold…). */
const canonicalizeStatusKey = key => {
  const raw = String(key || '').trim();
  if (!raw) return raw;
  if (raw === 'all' || ORDER_STATUSES.includes(raw)) return raw;

  const kebab = raw
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/_/g, '-')
    .toLowerCase();
  if (kebab === 'all' || ORDER_STATUSES.includes(kebab)) return kebab;

  const compact = kebab.replace(/-/g, '');
  const fromCompact = ORDER_STATUSES.find(
    status => status.replace(/-/g, '') === compact
  );
  return fromCompact || kebab;
};

const normalizeStatusCounts = counts => {
  if (!counts || typeof counts !== 'object' || Array.isArray(counts)) return {};
  const normalized = {};
  Object.entries(counts).forEach(([key, value]) => {
    const canonical = canonicalizeStatusKey(key);
    normalized[canonical] =
      (Number(normalized[canonical]) || 0) + (Number(value) || 0);
  });
  return normalized;
};

const withAgentAvatar = record => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    return record;
  }
  const next = { ...record };
  const selfAvatar = agentAvatarUrl(next);
  if (selfAvatar) next.avatarUrl = selfAvatar;
  if (next.agent) {
    next.agent = {
      ...next.agent,
      avatarUrl: agentAvatarUrl(next.agent),
    };
  }
  if (next.actionByAgent) {
    next.actionByAgent = {
      ...next.actionByAgent,
      avatarUrl: agentAvatarUrl(next.actionByAgent),
    };
  }
  if (Array.isArray(next.orderLogs)) {
    next.orderLogs = next.orderLogs.map(log =>
      log?.actionByAgent
        ? {
            ...log,
            actionByAgent: {
              ...log.actionByAgent,
              avatarUrl: agentAvatarUrl(log.actionByAgent),
            },
          }
        : log
    );
  }
  return next;
};

const unwrap = response => {
  const data = response?.data || {};
  const rawMeta = data.meta || {};
  // Preserve hyphenated status keys (on-hold, ready-to-go, in-transit).
  // deep camelcaseKeys would turn them into onHold / readyToGo / inTransit.
  const rawStatusCounts =
    rawMeta.status_counts || rawMeta.statusCounts || rawMeta.counts;
  const meta = camelcaseKeys(rawMeta, { deep: true });
  if (rawStatusCounts) {
    meta.statusCounts = normalizeStatusCounts(rawStatusCounts);
  }
  const rawRecords = data.payload ?? data.data ?? null;
  let recordsSource = [];
  if (rawRecords != null) {
    recordsSource = rawRecords;
  } else if (Array.isArray(data)) {
    recordsSource = data;
  }
  const camelRecords = camelcaseKeys(recordsSource, { deep: true });
  return {
    records: Array.isArray(camelRecords)
      ? camelRecords.map(record => withAgentAvatar(record))
      : withAgentAvatar(camelRecords),
    meta,
  };
};

/** Single-resource responses (show/create/update) may be root JSON or wrapped in payload. */
const extractRecord = response => {
  const data = response?.data;
  if (!data || typeof data !== 'object') return null;

  const payload = data.payload ?? data.web_order ?? data.webOrder;
  if (payload != null) {
    const candidate = Array.isArray(payload) ? payload[0] : payload;
    if (candidate?.id != null) {
      return withAgentAvatar(camelcaseKeys(candidate, { deep: true }));
    }
  }

  if (data.id != null) {
    return withAgentAvatar(camelcaseKeys(data, { deep: true }));
  }

  const { records } = unwrap(response);
  if (Array.isArray(records)) {
    const match = records.find(entry => entry && entry.id != null);
    if (match) return match;
  } else if (records && typeof records === 'object' && records.id != null) {
    return records;
  }

  return null;
};

const errorMessage = error =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'Request failed';

const upsert = (records, record) => {
  const item = withAgentAvatar(camelcaseKeys(record || {}, { deep: true }));
  if (item.id == null) return item;
  const index = records.findIndex(
    entry => Number(entry.id) === Number(item.id)
  );
  if (index === -1) {
    records.unshift(item);
  } else {
    // Replace in place so Vue reactivity triggers on the array element.
    records.splice(index, 1, { ...records[index], ...item });
  }
  return item;
};

const mergeExisting = (records, record) => {
  const item = withAgentAvatar(camelcaseKeys(record || {}, { deep: true }));
  if (item.id == null) return item;
  const index = records.findIndex(
    entry => Number(entry.id) === Number(item.id)
  );
  if (index !== -1) {
    records.splice(index, 1, { ...records[index], ...item });
  }
  return item;
};

/**
 * Keep every mounted order view backed by the same mutation result.
 * WebOrderAgentOpsTab uses a separate daily-queue collection, so only updating
 * `orders`/`currentOrder` leaves that table stale until a hard refresh.
 */
const patchOrderCollections = (state, record) => {
  const item = upsert(state.orders, record);
  mergeExisting(state.agentAssignedOrders, item);
  mergeExisting(state.assignments, item);
  if (Number(state.currentOrder?.id) === Number(item.id)) {
    state.currentOrder = { ...state.currentOrder, ...item };
  }
  return item;
};

const mergeOrderCollections = (state, record) => {
  const item = mergeExisting(state.orders, record);
  mergeExisting(state.agentAssignedOrders, item);
  mergeExisting(state.assignments, item);
  if (Number(state.currentOrder?.id) === Number(item.id)) {
    state.currentOrder = { ...state.currentOrder, ...item };
  }
  return item;
};

const dropOrderFromList = (state, id) => {
  state.orders = state.orders.filter(item => Number(item.id) !== Number(id));
};

const findOrderSnapshot = (state, id) => {
  const orderId = Number(id);
  return (
    state.orders.find(entry => Number(entry.id) === orderId) ||
    (Number(state.currentOrder?.id) === orderId ? state.currentOrder : null) ||
    state.agentAssignedOrders.find(entry => Number(entry.id) === orderId)
  );
};

const normalizeNavIds = ids =>
  (ids || []).map(id => Number(id)).filter(Boolean);

const orderMatchesLockStatus = (order, lockStatus) => {
  if (!lockStatus) return true;
  return (
    canonicalizeStatusKey(order?.status) === canonicalizeStatusKey(lockStatus)
  );
};

const parseOrdersNavSource = sourceKey => {
  const parts = String(sourceKey || '').split(':');
  if (parts[0] !== 'orders') return null;
  const rest = parts.slice(3);
  const duplicatePhones = rest[rest.length - 1] === 'dup';
  if (duplicatePhones) rest.pop();
  let q = '';
  if (rest.length) {
    try {
      q = decodeURIComponent(rest.join(':'));
    } catch {
      q = rest.join(':');
    }
  }
  return {
    status: parts[1] || '',
    page: Number(parts[2]) || 1,
    q,
    duplicatePhones,
  };
};

const emptyAssignmentCount = () => ({ pending: 0, onHold: 0 });

const normalizeAssignmentCountEntry = raw => {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return emptyAssignmentCount();
  }
  return {
    pending: Number(raw.pending) || 0,
    onHold: Number(raw.onHold ?? raw.on_hold ?? raw['on-hold']) || 0,
  };
};

const normalizeAssignmentCounts = agents => {
  if (!agents || typeof agents !== 'object' || Array.isArray(agents)) return {};
  return Object.fromEntries(
    Object.entries(agents).map(([id, counts]) => [
      String(id),
      normalizeAssignmentCountEntry(counts),
    ])
  );
};

/** Slim created events omit most fields, so skip the chip when extra filters/search are on. */
const matchesLiveNewChip = (state, data) => {
  const tab = state.ordersListStatus;
  if (tab && data.status !== tab) return false;
  if (state.ordersListHasFilters) return false;
  if (state.ordersListQuery) return false;
  return true;
};

export const useWebOrderStore = defineStore('webOrder', {
  state: () => ({
    orders: [],
    orderMeta: {},
    statusCounts: {},
    duplicatePhoneStats: {
      orderCount: 0,
      phoneCount: 0,
      phones: [],
      status: null,
    },
    pickingChecklist: { orderCount: 0, products: [] },
    products: [],
    productMeta: {},
    stockRows: [],
    stockMeta: {},
    stockSummary: {
      available: 0,
      processing: 0,
      inTransit: 0,
      returning: 0,
      shortage: 0,
    },
    stockLogs: [],
    stockLogMeta: {},
    vendors: [],
    vendorMeta: {},
    warehouses: [],
    warehouseMeta: {},
    purchases: [],
    purchaseMeta: {},
    bills: [],
    billMeta: {},
    bulkAdjustSeed: { warehouseId: '', rows: [] },
    customers: [],
    customerMeta: {},
    customerProfile: null,
    webCustomerProfile: null,
    wizardCustomer: null,
    currentOrder: null,
    currentProduct: null,
    orderNavQueue: {
      ids: [],
      sourceKey: '',
      lockStatus: '',
      cursorIndex: null,
    },
    assignments: [],
    assignmentCounts: {},
    agents: [],
    agentPerformance: {},
    agentOverviewAnalytics: {},
    agentAssignedOrders: [],
    agentAssignedOrdersMeta: {},
    agentAssignedAllMeta: {},
    stores: [],
    deliveryPartners: [],
    pickupAddresses: [],
    webOrderSetting: null,
    orderFilters: defaultOrderFilters(),
    orderFiltersDraft: defaultOrderFilters(),
    loading: {},
    error: '',
    errors: {},
    ordersFetchId: 0,
    productsFetchId: 0,
    stockFetchId: 0,
    stockLogsFetchId: 0,
    vendorsFetchId: 0,
    warehousesFetchId: 0,
    purchasesFetchId: 0,
    billsFetchId: 0,
    liveNewOrderCount: 0,
    ordersListStatus: null,
    ordersListQuery: '',
    ordersListHasFilters: false,
  }),
  getters: {
    hasActiveOrderFilters: state => hasActiveOrderFilters(state.orderFilters),
    activeOrderFilterChips: state => {
      const chips = [];
      const filters = state.orderFilters;
      const pushList = (key, labelKey, values, optionKey) => {
        if (!values?.length) return;
        values.forEach(value => {
          chips.push({
            key: `${key}:${value}`,
            type: key,
            value,
            label: optionKey
              ? `${labelKey}: ${value}`
              : `${labelKey}: ${value}`,
          });
        });
      };

      pushList(
        'filterDeliveryTypes',
        'deliveryType',
        filters.filterDeliveryTypes
      );
      pushList(
        'filterOrderStatuses',
        'orderStatus',
        filters.filterOrderStatuses
      );
      pushList('filterOrderSources', 'orderSource', filters.filterOrderSources);
      pushList('filterCustomerTags', 'customerTag', filters.filterCustomerTags);

      if (
        filters.filterDateCreation?.startDate ||
        filters.filterDateCreation?.endDate
      ) {
        chips.push({
          key: 'filterDateCreation',
          type: 'filterDateCreation',
          label: 'creationDate',
          value: filters.filterDateCreation,
        });
      }
      if (
        filters.filterDateShipping?.startDate ||
        filters.filterDateShipping?.endDate
      ) {
        chips.push({
          key: 'filterDateShipping',
          type: 'filterDateShipping',
          label: 'shippingDate',
          value: filters.filterDateShipping,
        });
      }
      if (
        filters.filterDateTransition?.startDate ||
        filters.filterDateTransition?.endDate
      ) {
        chips.push({
          key: 'filterDateTransition',
          type: 'filterDateTransition',
          label: 'transitionDate',
          value: filters.filterDateTransition,
        });
      }

      const invoice = filters.filterRangeInvoice;
      if (invoice?.startInvoice || invoice?.endInvoice) {
        chips.push({
          key: 'filterRangeInvoice',
          type: 'filterRangeInvoice',
          label: 'invoiceRange',
          value: invoice,
        });
      }

      const orderValue = filters.filterRangeOrderValue;
      if (orderValue?.min || orderValue?.max) {
        chips.push({
          key: 'filterRangeOrderValue',
          type: 'filterRangeOrderValue',
          label: 'orderValue',
          value: orderValue,
        });
      }

      const productCount = filters.filterRangeProductCount;
      if (productCount?.minQty || productCount?.maxQty || productCount?.sku) {
        chips.push({
          key: 'filterRangeProductCount',
          type: 'filterRangeProductCount',
          label: 'productCount',
          value: productCount,
        });
      }

      (filters.filterDeliveryPartnerIds || []).forEach(partnerId => {
        const isUnassigned = String(partnerId).toLowerCase() === 'unassigned';
        const partner = isUnassigned
          ? null
          : state.deliveryPartners.find(
              item => Number(item.id) === Number(partnerId)
            );
        chips.push({
          key: `filterDeliveryPartnerIds:${partnerId}`,
          type: 'filterDeliveryPartnerIds',
          label: 'deliveryPartner',
          value: partnerId,
          name: isUnassigned
            ? 'Unassigned'
            : partner?.name || partner?.provider,
        });
      });

      (filters.filterCourierStatuses || []).forEach(status => {
        chips.push({
          key: `filterCourierStatuses:${status}`,
          type: 'filterCourierStatuses',
          label: 'courierStatus',
          value: status,
        });
      });

      if (filters.filterPickupAddressId) {
        const pickup = state.pickupAddresses.find(
          item => Number(item.id) === Number(filters.filterPickupAddressId)
        );
        chips.push({
          key: 'filterPickupAddressId',
          type: 'filterPickupAddressId',
          label: 'pickupAddress',
          value: filters.filterPickupAddressId,
          name: pickup?.label,
        });
      }

      if (filters.filterAssignedTo) {
        const agent = state.agents.find(
          item => Number(item.id) === Number(filters.filterAssignedTo)
        );
        chips.push({
          key: 'filterAssignedTo',
          type: 'filterAssignedTo',
          label: 'assignedTo',
          value: filters.filterAssignedTo,
          name: agent?.name,
        });
      }

      if (filters.filterActionByUserId) {
        const agent = state.agents.find(
          item => Number(item.id) === Number(filters.filterActionByUserId)
        );
        chips.push({
          key: 'filterActionByUserId',
          type: 'filterActionByUserId',
          label: 'actionBy',
          value: filters.filterActionByUserId,
          name: agent?.name,
        });
      }

      if (filters.filterActionStatus) {
        chips.push({
          key: 'filterActionStatus',
          type: 'filterActionStatus',
          label: 'actionStatus',
          value: filters.filterActionStatus,
        });
      }

      if (
        filters.filterHasInternalNotes !== null &&
        filters.filterHasInternalNotes !== ''
      ) {
        chips.push({
          key: 'filterHasInternalNotes',
          type: 'filterHasInternalNotes',
          label: 'internalNotes',
          value: filters.filterHasInternalNotes,
        });
      }
      if (
        filters.filterHasDiscount !== null &&
        filters.filterHasDiscount !== ''
      ) {
        chips.push({
          key: 'filterHasDiscount',
          type: 'filterHasDiscount',
          label: 'discount',
          value: filters.filterHasDiscount,
        });
      }
      if (
        filters.filterIsAdvancePaid !== null &&
        filters.filterIsAdvancePaid !== ''
      ) {
        chips.push({
          key: 'filterIsAdvancePaid',
          type: 'filterIsAdvancePaid',
          label: 'advancePaid',
          value: filters.filterIsAdvancePaid,
        });
      }
      if (
        filters.filterIsInvoicePrinted !== null &&
        filters.filterIsInvoicePrinted !== ''
      ) {
        chips.push({
          key: 'filterIsInvoicePrinted',
          type: 'filterIsInvoicePrinted',
          label: 'invoicePrinted',
          value: filters.filterIsInvoicePrinted,
        });
      }
      if (filters.filterHasUpsell !== null && filters.filterHasUpsell !== '') {
        chips.push({
          key: 'filterHasUpsell',
          type: 'filterHasUpsell',
          label: 'upsell',
          value: filters.filterHasUpsell,
        });
      }
      if (filters.filterDuplicatePhones === true) {
        chips.push({
          key: 'filterDuplicatePhones',
          type: 'filterDuplicatePhones',
          label: 'duplicatePhones',
          value: true,
        });
      }
      if (filters.filterDuplicatePhoneMinCount) {
        chips.push({
          key: 'filterDuplicatePhoneMinCount',
          type: 'filterDuplicatePhoneMinCount',
          label: 'duplicateMinCount',
          value: filters.filterDuplicatePhoneMinCount,
        });
      }
      if (String(filters.filterDuplicatePhone || '').trim()) {
        chips.push({
          key: 'filterDuplicatePhone',
          type: 'filterDuplicatePhone',
          label: 'duplicatePhone',
          value: String(filters.filterDuplicatePhone).trim(),
        });
      }

      return chips;
    },
    deliveryTypeOptions: () =>
      DELIVERY_TYPES.map(value => ({
        id: value,
        name: value,
        type: 'deliveryType',
      })),
    orderSourceOptions: () =>
      ORDER_SOURCES.map(value => ({
        id: value,
        name: value,
        type: 'orderSource',
      })),
    orderStatusFilterOptions: () =>
      ORDER_STATUSES.map(value => ({
        id: value,
        name: value,
        type: 'orderStatus',
      })),
    totalOrders: state =>
      Number(
        state.orderMeta.totalCount ||
          state.orderMeta.total ||
          state.orderMeta.count ||
          state.orders.length
      ),
    totalProducts: state =>
      Number(
        state.productMeta.totalCount ||
          state.productMeta.total ||
          state.productMeta.count ||
          state.products.length
      ),
    totalCustomers: state =>
      Number(
        state.customerMeta.totalCount ||
          state.customerMeta.total ||
          state.customers.length
      ),
    activeWarehouses: state =>
      (state.warehouses || []).filter(warehouse => warehouse.active !== false),
    hasMultipleWarehouses: state =>
      (state.warehouses || []).filter(warehouse => warehouse.active !== false)
        .length > 1,
    defaultWarehouse: state =>
      (state.warehouses || []).find(warehouse => warehouse.isDefault) ||
      (state.warehouses || []).find(warehouse => warehouse.active !== false) ||
      null,
  },
  actions: {
    clearError(key) {
      if (key) {
        this.errors[key] = '';
        if (key === 'orders') this.error = '';
        return;
      }
      this.error = '';
      this.errors = {};
    },
    async request(key, callback, { silent = false } = {}) {
      this.loading[key] = true;
      if (!silent) {
        this.errors[key] = '';
        // Only clear the list-level error when fetching orders. Mutation /
        // assignment failures must not blank the orders table via store.error.
        if (key === 'orders') this.error = '';
      }
      try {
        return await callback();
      } catch (error) {
        if (!silent) {
          const message = errorMessage(error);
          this.errors[key] = message;
          this.error = message;
        }
        throw error;
      } finally {
        this.loading[key] = false;
      }
    },
    adjustStatusCounts(fromStatus, toStatus) {
      const from = canonicalizeStatusKey(fromStatus);
      const to = canonicalizeStatusKey(toStatus);
      if (from && from === to) return;
      const counts = { ...this.statusCounts };
      const bump = (key, delta) => {
        if (!key) return;
        const next = Math.max(0, Number(counts[key] || 0) + delta);
        counts[key] = next;
      };
      if (from) bump(from, -1);
      if (to) bump(to, 1);
      if (!from && to) bump('all', 1);
      if (from && !to) bump('all', -1);
      this.statusCounts = counts;
    },
    statusCountFor(status) {
      const key = canonicalizeStatusKey(status);
      if (key === 'all') {
        return Number(
          this.statusCounts.all ??
            this.orderMeta.totalCount ??
            this.orders.length ??
            0
        );
      }
      return Number(this.statusCounts[key] || 0);
    },
    async fetchOrders(params = {}) {
      this.ordersFetchId = (this.ordersFetchId || 0) + 1;
      const requestId = this.ordersFetchId;
      return this.request('orders', async () => {
        const filterPayload = buildFilterPayload(this.orderFilters);
        const listParams = Object.fromEntries(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ''
          )
        );
        const requestParams = { ...listParams, ...filterPayload };
        const response =
          Object.keys(filterPayload).length > 0
            ? await webOrdersAPI.filter(requestParams)
            : await webOrdersAPI.list(listParams);
        // Drop stale responses from overlapping fetches.
        if (requestId !== this.ordersFetchId) return;
        const { records, meta } = unwrap(response);
        this.orders = Array.isArray(records) ? records : [];
        this.liveNewOrderCount = 0;
        this.ordersListStatus = listParams.status || null;
        this.ordersListQuery = String(listParams.q || '').trim();
        this.ordersListHasFilters =
          Object.keys(filterPayload).length > 0 ||
          Boolean(listParams.duplicate_phones);
        this.orderMeta = meta;
        if (meta.statusCounts) {
          this.statusCounts = meta.statusCounts;
        }
        const dup = meta.duplicatePhoneStats || {};
        // Deferred list responses leave stats empty — keep prior row until
        // fetchDuplicatePhoneStats lands (unless status tab changed).
        if (!dup.deferred) {
          this.duplicatePhoneStats = {
            orderCount: Number(dup.orderCount || 0),
            phoneCount: Number(dup.phoneCount || 0),
            phones: Array.isArray(dup.phones) ? dup.phones : [],
            status: dup.status || null,
          };
        } else if (
          (dup.status || null) !== (this.duplicatePhoneStats.status || null)
        ) {
          this.duplicatePhoneStats = {
            orderCount: 0,
            phoneCount: 0,
            phones: [],
            status: dup.status || null,
          };
        }
      });
    },
    async fetchDuplicatePhoneStats(status) {
      this.duplicateStatsFetchId = (this.duplicateStatsFetchId || 0) + 1;
      const requestId = this.duplicateStatsFetchId;
      if (!status) {
        this.duplicatePhoneStats = {
          orderCount: 0,
          phoneCount: 0,
          phones: [],
          status: null,
        };
        return;
      }
      try {
        const response = await webOrdersAPI.duplicatePhoneStats({ status });
        if (requestId !== this.duplicateStatsFetchId) return;
        const body = response?.data || {};
        const dup = body.payload || body;
        this.duplicatePhoneStats = {
          orderCount: Number(dup.orderCount || dup.order_count || 0),
          phoneCount: Number(dup.phoneCount || dup.phone_count || 0),
          phones: Array.isArray(dup.phones) ? dup.phones : [],
          status: dup.status || status,
        };
      } catch {
        /* banner stays hidden on failure; list already rendered */
      }
    },
    async fetchPickingChecklist(params = {}) {
      return this.request('pickingChecklist', async () => {
        const filterPayload = buildFilterPayload(this.orderFilters);
        const listParams = Object.fromEntries(
          Object.entries(params).filter(
            ([, value]) => value !== undefined && value !== null && value !== ''
          )
        );
        const response = await webOrdersAPI.pickingChecklist({
          ...listParams,
          ...filterPayload,
        });
        const data = response?.data?.payload || response?.data || {};
        const products = camelcaseKeys(data.products || [], { deep: true });
        this.pickingChecklist = {
          orderCount: Number(data.order_count ?? data.orderCount ?? 0),
          products: Array.isArray(products) ? products : [],
        };
        return this.pickingChecklist;
      });
    },
    resetOrderFilters() {
      this.orderFilters = defaultOrderFilters();
      this.orderFiltersDraft = defaultOrderFilters();
    },
    syncOrderFiltersDraft() {
      this.orderFiltersDraft = JSON.parse(JSON.stringify(this.orderFilters));
    },
    applyOrderFiltersDraft() {
      this.orderFilters = JSON.parse(JSON.stringify(this.orderFiltersDraft));
    },
    updateOrderFiltersDraft(partial = {}) {
      this.orderFiltersDraft = { ...this.orderFiltersDraft, ...partial };
    },
    removeOrderFilterChip(chip) {
      const next = JSON.parse(JSON.stringify(this.orderFilters));
      const { type, value } = chip;

      if (type === 'filterDeliveryTypes') {
        next.filterDeliveryTypes = next.filterDeliveryTypes.filter(
          v => v !== value
        );
      } else if (type === 'filterOrderStatuses') {
        next.filterOrderStatuses = next.filterOrderStatuses.filter(
          v => v !== value
        );
      } else if (type === 'filterOrderSources') {
        next.filterOrderSources = next.filterOrderSources.filter(
          v => v !== value
        );
      } else if (type === 'filterCustomerTags') {
        next.filterCustomerTags = next.filterCustomerTags.filter(
          v => v !== value
        );
      } else if (type === 'filterDateCreation') {
        next.filterDateCreation = { startDate: '', endDate: '' };
      } else if (type === 'filterDateShipping') {
        next.filterDateShipping = { startDate: '', endDate: '' };
      } else if (type === 'filterDateTransition') {
        next.filterDateTransition = { startDate: '', endDate: '' };
      } else if (type === 'filterRangeInvoice') {
        next.filterRangeInvoice = { startInvoice: '', endInvoice: '' };
      } else if (type === 'filterRangeOrderValue') {
        next.filterRangeOrderValue = { min: '', max: '' };
      } else if (type === 'filterRangeProductCount') {
        next.filterRangeProductCount = { minQty: '', maxQty: '', sku: '' };
      } else if (type === 'filterDeliveryPartnerIds') {
        next.filterDeliveryPartnerIds = (
          next.filterDeliveryPartnerIds || []
        ).filter(id => String(id) !== String(value));
      } else if (type === 'filterCourierStatuses') {
        next.filterCourierStatuses = (next.filterCourierStatuses || []).filter(
          status => status !== value
        );
      } else if (type === 'filterPickupAddressId') {
        next.filterPickupAddressId = '';
      } else if (type === 'filterAssignedTo') {
        next.filterAssignedTo = '';
      } else if (type === 'filterAssignedOn') {
        next.filterAssignedOn = { startDate: '', endDate: '' };
      } else if (type === 'filterActionByUserId') {
        next.filterActionByUserId = '';
      } else if (type === 'filterActionStatus') {
        next.filterActionStatus = '';
      } else if (type === 'filterHasInternalNotes') {
        next.filterHasInternalNotes = null;
      } else if (type === 'filterHasDiscount') {
        next.filterHasDiscount = null;
      } else if (type === 'filterIsAdvancePaid') {
        next.filterIsAdvancePaid = null;
      } else if (type === 'filterIsInvoicePrinted') {
        next.filterIsInvoicePrinted = null;
      } else if (type === 'filterHasUpsell') {
        next.filterHasUpsell = null;
      } else if (type === 'filterDuplicatePhones') {
        next.filterDuplicatePhones = null;
      } else if (type === 'filterDuplicatePhoneMinCount') {
        next.filterDuplicatePhoneMinCount = '';
      } else if (type === 'filterDuplicatePhone') {
        next.filterDuplicatePhone = '';
      }

      this.orderFilters = next;
      this.orderFiltersDraft = JSON.parse(JSON.stringify(next));
    },
    setOrderNavQueue(ids, sourceKey = 'orders_list', lockStatus = '') {
      this.orderNavQueue = {
        ids: normalizeNavIds(ids),
        sourceKey,
        lockStatus: lockStatus || '',
        cursorIndex: null,
      };
    },
    dropFromOrderNavQueue(id) {
      const queue = this.orderNavQueue || {};
      const ids = [...(queue.ids || [])];
      const index = ids.findIndex(item => Number(item) === Number(id));
      if (index < 0) return;
      ids.splice(index, 1);
      this.orderNavQueue = {
        ...queue,
        ids,
        cursorIndex: index,
      };
    },
    syncOrderNavQueueAfterStatus(id, nextStatus) {
      const lockStatus = this.orderNavQueue?.lockStatus;
      if (!lockStatus || !nextStatus) return;
      if (
        canonicalizeStatusKey(nextStatus) === canonicalizeStatusKey(lockStatus)
      ) {
        return;
      }
      this.dropFromOrderNavQueue(id);
    },
    rebuildNavQueueIds(nextIds, currentId) {
      const previousIds = this.orderNavQueue?.ids || [];
      const previousCursor = this.orderNavQueue?.cursorIndex;
      const ids = normalizeNavIds(nextIds);
      const numericCurrent = Number(currentId);
      const stillInQueue = ids.some(id => id === numericCurrent);
      let cursorIndex = previousCursor;
      if (stillInQueue) {
        cursorIndex = null;
      } else if (previousCursor != null) {
        const hint = previousIds[previousCursor];
        const mapped = ids.findIndex(id => Number(id) === Number(hint));
        cursorIndex = mapped >= 0 ? mapped : ids.length;
      } else {
        const former = previousIds.findIndex(
          id => Number(id) === numericCurrent
        );
        if (former >= 0) {
          const hint = previousIds[former + 1];
          const mapped = hint
            ? ids.findIndex(id => Number(id) === Number(hint))
            : -1;
          cursorIndex = mapped >= 0 ? mapped : ids.length;
        }
      }
      this.orderNavQueue = {
        ...this.orderNavQueue,
        ids,
        cursorIndex,
      };
    },
    async refreshOrderNavQueue(currentId) {
      const queue = this.orderNavQueue || {};
      const lockStatus = queue.lockStatus;
      const sourceKey = String(queue.sourceKey || '');
      if (!lockStatus || !sourceKey) return;

      try {
        let records = [];
        if (sourceKey.startsWith('orders:')) {
          const parsed = parseOrdersNavSource(sourceKey);
          if (!parsed) return;
          const perPage =
            Number(this.orderMeta?.perPage || this.orderMeta?.per_page) ||
            undefined;
          await this.fetchOrders({
            page: parsed.page,
            status: lockStatus,
            q: parsed.q || undefined,
            per_page: perPage,
            duplicate_phones: parsed.duplicatePhones ? true : undefined,
          });
          records = this.orders || [];
        } else if (sourceKey.startsWith('agent_profile:')) {
          const parts = sourceKey.split(':');
          const agentId = Number(parts[1]);
          if (!agentId) return;
          const tablePage =
            Number(String(parts[3] || 'p1').replace(/^p/, '')) || 1;
          await this.fetchAgentAssignedOrders(agentId, {
            dailyDate: parts[2],
            page: tablePage,
            perPage: 20,
          });
          records = this.agentAssignedOrders || [];
        } else {
          return;
        }

        const nextIds = records
          .filter(order => orderMatchesLockStatus(order, lockStatus))
          .map(order => Number(order.id));
        this.rebuildNavQueueIds(nextIds, currentId);
      } catch {
        this.clearError('orders');
        this.clearError('agentAssignedOrders');
      }
    },
    async updateOrder(id, attributes) {
      return this.request('orderMutation', async () => {
        const previous = findOrderSnapshot(this, id);
        const previousStatus = previous?.status;
        const payload = { ...attributes };
        if (
          previous?.updatedAt &&
          payload.updatedAt == null &&
          payload.updated_at == null
        ) {
          payload.updatedAt = previous.updatedAt;
        }
        const optimisticStatus = payload?.status;
        const statusChanging =
          Boolean(optimisticStatus) &&
          Boolean(previousStatus) &&
          previousStatus !== optimisticStatus;

        // Apply status + counts before the network round-trip so a realtime
        // echo of the same update cannot double-decrement/increment tabs.
        if (statusChanging) {
          patchOrderCollections(this, { id, status: optimisticStatus });
          this.adjustStatusCounts(previousStatus, optimisticStatus);
        }

        try {
          const response = await webOrdersAPI.update(id, {
            web_order: snakecaseKeys(payload, { deep: true }),
          });
          const record = extractRecord(response);
          if (!record?.id) {
            throw new Error('Order update response missing id');
          }
          patchOrderCollections(this, record);
          if (
            statusChanging &&
            record?.status &&
            record.status !== optimisticStatus
          ) {
            this.adjustStatusCounts(optimisticStatus, record.status);
          } else if (
            !statusChanging &&
            previousStatus &&
            record?.status &&
            previousStatus !== record.status
          ) {
            this.adjustStatusCounts(previousStatus, record.status);
          }
          this.syncOrderNavQueueAfterStatus(id, record?.status);
          return record;
        } catch (error) {
          if (statusChanging && previous) {
            patchOrderCollections(this, previous);
            this.adjustStatusCounts(optimisticStatus, previousStatus);
          }
          if (error?.response?.status === 409) {
            try {
              await this.fetchOrder(id);
            } catch {
              /* keep rollback snapshot if refetch fails */
            }
            const stale = new Error(
              error.response?.data?.error ||
                'Order was updated by someone else. Refresh and try again.'
            );
            stale.code = 'stale_order';
            stale.response = error.response;
            throw stale;
          }
          throw error;
        }
      });
    },
    async createOrder(attributes) {
      return this.request('orderMutation', async () => {
        const response = await webOrdersAPI.create({
          web_order: snakecaseKeys(attributes, { deep: true }),
        });
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Created order response missing id');
        }
        upsert(this.orders, record);
        if (record.status) this.adjustStatusCounts(null, record.status);
        this.error = '';
        this.errors.orderMutation = '';
        return record;
      });
    },
    async bulkUpdateOrders(ids, attributes) {
      return this.request('orderMutation', async () => {
        const idSet = new Set((ids || []).map(id => Number(id)));
        const nextStatus = attributes?.status;
        const previousById = new Map();
        if (nextStatus) {
          const visibleOrders = [
            ...this.orders,
            ...this.agentAssignedOrders,
            ...(this.currentOrder ? [this.currentOrder] : []),
          ];
          visibleOrders.forEach(order => {
            if (!idSet.has(Number(order.id))) return;
            const orderId = Number(order.id);
            if (!previousById.has(orderId)) {
              previousById.set(orderId, order);
              if (order.status && order.status !== nextStatus) {
                this.adjustStatusCounts(order.status, nextStatus);
              }
            }
          });
          // Update local rows before await so realtime echoes are no-ops.
          idSet.forEach(id =>
            patchOrderCollections(this, { id, ...attributes })
          );
        }

        try {
          const response = await webOrdersAPI.bulkUpdate(
            ids,
            snakecaseKeys(attributes, { deep: true })
          );
          const { records } = unwrap(response);
          if (Array.isArray(records) && records.length) {
            records.forEach(record => patchOrderCollections(this, record));
          } else if (!nextStatus) {
            idSet.forEach(id =>
              patchOrderCollections(this, { id, ...attributes })
            );
          }
          return Array.isArray(records) ? records : [];
        } catch (error) {
          if (previousById.size) {
            previousById.forEach(previous => {
              patchOrderCollections(this, previous);
              if (previous.status && previous.status !== nextStatus) {
                this.adjustStatusCounts(nextStatus, previous.status);
              }
            });
          }
          throw error;
        }
      });
    },
    async markInvoicePrinted(ids) {
      const idSet = new Set((ids || []).map(id => Number(id)));
      return this.request('orderMutation', async () => {
        await webOrdersAPI.markInvoicePrinted(ids);
        const printedAt = new Date().toISOString();
        const stamp = order =>
          idSet.has(Number(order.id))
            ? {
                ...order,
                invoicePrintedAt: order.invoicePrintedAt || printedAt,
              }
            : order;
        this.orders = this.orders.map(stamp);
        if (idSet.has(Number(this.currentOrder?.id))) {
          this.currentOrder = stamp(this.currentOrder);
        }
      });
    },
    async fetchProducts(params = {}) {
      this.productsFetchId = (this.productsFetchId || 0) + 1;
      const requestId = this.productsFetchId;
      return this.request('products', async () => {
        const { records, meta } = unwrap(await webProductsAPI.list(params));
        // Ignore stale responses so overlapping search/store fetches cannot
        // wipe a fresher product list (looks like "loaded then vanished").
        if (requestId !== this.productsFetchId) return;
        this.products = Array.isArray(records) ? records : [];
        this.productMeta = meta;
      });
    },
    async fetchStock(params = {}) {
      this.stockFetchId = (this.stockFetchId || 0) + 1;
      const requestId = this.stockFetchId;
      return this.request('stock', async () => {
        const { records, meta } = unwrap(await inventoryStockAPI.list(params));
        if (requestId !== this.stockFetchId) return;
        this.stockRows = Array.isArray(records) ? records : [];
        this.stockMeta = meta;
        this.stockSummary = {
          available: Number(meta.summary?.available) || 0,
          processing: Number(meta.summary?.processing) || 0,
          inTransit: Number(meta.summary?.inTransit) || 0,
          returning: Number(meta.summary?.returning) || 0,
          shortage: Number(meta.summary?.shortage) || 0,
        };
      });
    },
    async fetchStockLogs(params = {}) {
      this.stockLogsFetchId = (this.stockLogsFetchId || 0) + 1;
      const requestId = this.stockLogsFetchId;
      return this.request('stockLogs', async () => {
        const { records, meta } = unwrap(await inventoryLogsAPI.list(params));
        if (requestId !== this.stockLogsFetchId) return;
        this.stockLogs = Array.isArray(records) ? records : [];
        this.stockLogMeta = meta;
      });
    },
    primeBulkAdjust(payload = {}) {
      this.bulkAdjustSeed = {
        warehouseId: payload.warehouseId ? String(payload.warehouseId) : '',
        rows: Array.isArray(payload.rows) ? payload.rows : [],
      };
    },
    consumeBulkAdjustSeed() {
      const seed = this.bulkAdjustSeed || { warehouseId: '', rows: [] };
      this.bulkAdjustSeed = { warehouseId: '', rows: [] };
      return seed;
    },
    async listStock(params = {}) {
      const { records, meta } = unwrap(await inventoryStockAPI.list(params));
      return {
        records: Array.isArray(records) ? records : [],
        meta,
      };
    },
    async adjustStock(payload = {}) {
      return this.request('stockAdjust', async () => {
        const hasLines = Array.isArray(payload.lines) && payload.lines.length;
        const body = hasLines
          ? {
              warehouseId: payload.warehouseId,
              lines: payload.lines.map(line => ({
                webProductId: line.webProductId,
                kind: line.kind,
                quantity: Number(line.quantity),
                reason: line.reason || undefined,
                expiresOn: line.expiresOn || undefined,
                direction:
                  line.kind === 'correction' ? line.direction : undefined,
              })),
            }
          : {
              webProductIds: payload.webProductIds,
              kind: payload.kind,
              quantity: Number(payload.quantity),
            };
        if (!hasLines) {
          if (payload.reason) body.reason = payload.reason;
          if (payload.expiresOn) body.expiresOn = payload.expiresOn;
          if (payload.kind === 'correction' && payload.direction) {
            body.direction = payload.direction;
          }
          if (payload.warehouseId) body.warehouseId = payload.warehouseId;
        }
        const response = await inventoryStockAPI.adjust(
          snakecaseKeys(body, { deep: true })
        );
        return unwrap(response);
      });
    },
    async fetchVendors(params = {}) {
      this.vendorsFetchId = (this.vendorsFetchId || 0) + 1;
      const requestId = this.vendorsFetchId;
      return this.request('vendors', async () => {
        const { records, meta } = unwrap(
          await inventoryVendorsAPI.list(params)
        );
        if (requestId !== this.vendorsFetchId) return;
        this.vendors = Array.isArray(records) ? records : [];
        this.vendorMeta = meta;
      });
    },
    async saveVendor(attributes) {
      return this.request('vendorMutation', async () => {
        const payload = {
          web_vendor: snakecaseKeys(attributes, { deep: true }),
        };
        const response = attributes.id
          ? await inventoryVendorsAPI.update(attributes.id, payload)
          : await inventoryVendorsAPI.create(payload);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Vendor save response missing id');
        }
        upsert(this.vendors, record);
        return record;
      });
    },
    async deleteVendor(id) {
      return this.request('vendorMutation', async () => {
        await inventoryVendorsAPI.delete(id);
        this.vendors = this.vendors.filter(
          item => Number(item.id) !== Number(id)
        );
        this.products = this.products.map(product =>
          Number(product.webVendorId) === Number(id)
            ? { ...product, webVendorId: null, webVendor: null }
            : product
        );
      });
    },
    async fetchWarehouses(params = {}) {
      this.warehousesFetchId = (this.warehousesFetchId || 0) + 1;
      const requestId = this.warehousesFetchId;
      return this.request('warehouses', async () => {
        const { records, meta } = unwrap(
          await inventoryWarehousesAPI.list({ per_page: 300, ...params })
        );
        if (requestId !== this.warehousesFetchId) return;
        this.warehouses = Array.isArray(records) ? records : [];
        this.warehouseMeta = meta;
      });
    },
    async saveWarehouse(attributes) {
      return this.request('warehouseMutation', async () => {
        const payload = {
          web_warehouse: snakecaseKeys(attributes, { deep: true }),
        };
        const response = attributes.id
          ? await inventoryWarehousesAPI.update(attributes.id, payload)
          : await inventoryWarehousesAPI.create(payload);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Warehouse save response missing id');
        }
        upsert(this.warehouses, record);
        return record;
      });
    },
    async deleteWarehouse(id) {
      return this.request('warehouseMutation', async () => {
        await inventoryWarehousesAPI.delete(id);
        this.warehouses = this.warehouses.filter(
          item => Number(item.id) !== Number(id)
        );
      });
    },
    async transferStock(payload = {}) {
      return this.request('stockTransfer', async () => {
        const body = {
          webProductId: payload.webProductId,
          fromWarehouseId: payload.fromWarehouseId,
          toWarehouseId: payload.toWarehouseId,
          quantity: Number(payload.quantity),
        };
        const response = await inventoryStockAPI.transfer(
          snakecaseKeys(body, { deep: true })
        );
        return unwrap(response);
      });
    },
    async listPurchases(params = {}) {
      const { records, meta } = unwrap(
        await inventoryPurchasesAPI.list(params)
      );
      return {
        records: Array.isArray(records) ? records : [],
        meta,
      };
    },
    async listBills(params = {}) {
      const { records, meta } = unwrap(await inventoryBillsAPI.list(params));
      return {
        records: Array.isArray(records) ? records : [],
        meta,
      };
    },
    async fetchPurchases(params = {}) {
      this.purchasesFetchId = (this.purchasesFetchId || 0) + 1;
      const requestId = this.purchasesFetchId;
      return this.request('purchases', async () => {
        const { records, meta } = unwrap(
          await inventoryPurchasesAPI.list(params)
        );
        if (requestId !== this.purchasesFetchId) return;
        this.purchases = Array.isArray(records) ? records : [];
        this.purchaseMeta = meta;
      });
    },
    async savePurchase(attributes) {
      return this.request('purchaseMutation', async () => {
        const payload = {
          web_purchase: snakecaseKeys(attributes, { deep: true }),
        };
        const response = attributes.id
          ? await inventoryPurchasesAPI.update(attributes.id, payload)
          : await inventoryPurchasesAPI.create(payload);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Purchase save response missing id');
        }
        upsert(this.purchases, record);
        return record;
      });
    },
    async placePurchase(id) {
      return this.request('purchaseMutation', async () => {
        const record = extractRecord(await inventoryPurchasesAPI.place(id));
        upsert(this.purchases, record);
        return record;
      });
    },
    async markPurchaseReceiving(id) {
      return this.request('purchaseMutation', async () => {
        const record = extractRecord(await inventoryPurchasesAPI.receiving(id));
        upsert(this.purchases, record);
        return record;
      });
    },
    async receivePurchase(id) {
      return this.request('purchaseMutation', async () => {
        const record = extractRecord(await inventoryPurchasesAPI.receive(id));
        upsert(this.purchases, record);
        return record;
      });
    },
    async deletePurchase(id) {
      return this.request('purchaseMutation', async () => {
        await inventoryPurchasesAPI.delete(id);
        this.purchases = this.purchases.filter(
          item => Number(item.id) !== Number(id)
        );
      });
    },
    async loadPurchase(id) {
      return extractRecord(await inventoryPurchasesAPI.show(id));
    },
    async loadBill(id) {
      return extractRecord(await inventoryBillsAPI.show(id));
    },
    async fetchBills(params = {}) {
      this.billsFetchId = (this.billsFetchId || 0) + 1;
      const requestId = this.billsFetchId;
      return this.request('bills', async () => {
        const { records, meta } = unwrap(await inventoryBillsAPI.list(params));
        if (requestId !== this.billsFetchId) return;
        this.bills = Array.isArray(records) ? records : [];
        this.billMeta = meta;
      });
    },
    async saveBill(attributes) {
      return this.request('billMutation', async () => {
        const payload = {
          web_vendor_bill: snakecaseKeys(attributes, { deep: true }),
        };
        const response = attributes.id
          ? await inventoryBillsAPI.update(attributes.id, payload)
          : await inventoryBillsAPI.create(payload);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Bill save response missing id');
        }
        upsert(this.bills, record);
        return record;
      });
    },
    async issueBill(id, attributes = {}) {
      return this.request('billMutation', async () => {
        const record = extractRecord(
          await inventoryBillsAPI.issue(
            id,
            snakecaseKeys(attributes, { deep: true })
          )
        );
        upsert(this.bills, record);
        return record;
      });
    },
    async payBill(id, attributes = {}) {
      return this.request('billMutation', async () => {
        const record = extractRecord(
          await inventoryBillsAPI.pay(id, {
            payment: snakecaseKeys(attributes, { deep: true }),
          })
        );
        upsert(this.bills, record);
        return record;
      });
    },
    async deleteBill(id) {
      return this.request('billMutation', async () => {
        await inventoryBillsAPI.delete(id);
        this.bills = this.bills.filter(item => Number(item.id) !== Number(id));
      });
    },
    // Catalog lookup that does not share Pinia `products` / fetch-id with the
    // Products page (Edit Order was going empty when those raced).
    async listProducts(params = {}) {
      const { records, meta } = unwrap(await webProductsAPI.list(params));
      return {
        records: Array.isArray(records) ? records : [],
        meta,
      };
    },
    async listVendors(params = {}) {
      const { records, meta } = unwrap(await inventoryVendorsAPI.list(params));
      return {
        records: Array.isArray(records) ? records : [],
        meta,
      };
    },
    async fetchCustomers(params = {}) {
      return this.request('customers', async () => {
        const { records, meta } = unwrap(
          await webOrderCustomersAPI.list(params)
        );
        this.customers = Array.isArray(records) ? records : [];
        this.customerMeta = meta;
      });
    },
    async fetchCustomerProfile(customerId) {
      return this.request('customerProfile', async () => {
        const { records } = unwrap(
          await webOrderCustomersAPI.show(encodeURIComponent(customerId))
        );
        this.customerProfile = Array.isArray(records) ? records[0] : records;
        return this.customerProfile;
      });
    },
    async searchWebCustomers(params = {}) {
      return this.request('webCustomers', async () => {
        const { records, meta } = unwrap(await webCustomersAPI.list(params));
        return { records: Array.isArray(records) ? records : [], meta };
      });
    },
    async createWebCustomer(attributes) {
      return this.request('webCustomerMutation', async () => {
        const { records } = unwrap(
          await webCustomersAPI.create({
            web_customer: snakecaseKeys(attributes, { deep: true }),
          })
        );
        const record = Array.isArray(records) ? records[0] : records;
        return record;
      });
    },
    async updateWebCustomer(id, attributes) {
      return this.request('webCustomerMutation', async () => {
        const { records } = unwrap(
          await webCustomersAPI.update(id, {
            web_customer: snakecaseKeys(attributes, { deep: true }),
          })
        );
        const record = Array.isArray(records) ? records[0] : records;
        return record;
      });
    },
    async ensureWebCustomer(profile) {
      const existingId = profile?.webCustomerId || profile?.web_customer_id;
      if (existingId) {
        try {
          return await this.fetchWebCustomer(existingId);
        } catch {
          /* fall through to phone upsert */
        }
      }
      const phone = String(profile?.phone || '').replace(/\D/g, '');
      if (!phone) {
        throw new Error('Customer phone is required');
      }
      let address = {};
      if (profile?.address && typeof profile.address === 'object') {
        address = profile.address;
      } else if (profile?.addresses?.[0]?.label) {
        address = { address1: profile.addresses[0].label };
      }
      return this.createWebCustomer({
        name: profile?.name || `Customer ${phone.slice(-4)}`,
        phone,
        email: profile?.email || undefined,
        tags: profile?.tags || [],
        address,
      });
    },
    async fetchWebCustomer(id) {
      return this.request('webCustomerProfile', async () => {
        const { records } = unwrap(await webCustomersAPI.show(id));
        this.webCustomerProfile = Array.isArray(records) ? records[0] : records;
        return this.webCustomerProfile;
      });
    },
    async fetchCustomerTagSuggestions(query = '') {
      return this.request(
        'customerTags',
        async () => {
          const { records } = unwrap(await webCustomersAPI.tags(query));
          if (Array.isArray(records)) return records;
          if (Array.isArray(records?.tags)) return records.tags;
          return [];
        },
        { silent: true }
      );
    },
    async fetchOrder(id) {
      return this.request('orderDetail', async () => {
        const record = extractRecord(await webOrdersAPI.show(id));
        if (!record?.id) {
          throw new Error('Order detail response missing id');
        }
        this.currentOrder = record;
        upsert(this.orders, record);
        return record;
      });
    },
    async fetchProduct(id) {
      return this.request('productDetail', async () => {
        const record = extractRecord(await webProductsAPI.show(id));
        if (!record?.id) {
          throw new Error('Product detail response missing id');
        }
        this.currentProduct = record;
        upsert(this.products, record);
        return record;
      });
    },
    async saveProduct(product) {
      return this.request('productMutation', async () => {
        const { blobIds, keepImageIds, images, ...rest } = product;
        const payload = {
          web_product: {
            ...snakecaseKeys(rest, { deep: true }),
            blob_ids: blobIds || [],
            keep_image_ids: keepImageIds,
          },
        };
        const response = product.id
          ? await webProductsAPI.update(product.id, payload)
          : await webProductsAPI.create(payload);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Product save response missing id');
        }
        this.currentProduct = record;
        upsert(this.products, record);
        return record;
      });
    },
    async deleteProduct(id) {
      return this.request('productMutation', async () => {
        await webProductsAPI.delete(id);
        this.products = this.products.filter(item => item.id !== id);
        if (Number(this.currentProduct?.id) === Number(id)) {
          this.currentProduct = null;
        }
      });
    },
    async fetchAssignments() {
      // Silent: agent workload is supplementary. Failures must not replace
      // the orders list with a full-page error via the shared store.error.
      return this.request(
        'assignments',
        async () => {
          const [countsResult, agentResult] = await Promise.allSettled([
            webOrdersAPI.assignmentCounts(),
            webOrdersAPI.assignableAgents(),
          ]);
          if (agentResult.status !== 'fulfilled') {
            throw agentResult.reason;
          }
          const agentResponse = agentResult.value;
          let rawCounts = {};
          if (countsResult.status === 'fulfilled') {
            const countsData = countsResult.value?.data || {};
            const countsPayload = countsData.payload ?? countsData;
            rawCounts = countsPayload?.agents ?? {};
          }
          this.assignmentCounts = normalizeAssignmentCounts(
            camelcaseKeys(rawCounts, { deep: true })
          );
          this.assignments = [];
          const responseData = agentResponse?.data || {};
          const agentPayload = responseData.payload ?? responseData;
          const rawAgentList = Array.isArray(agentPayload?.agents)
            ? agentPayload.agents
            : [];
          const accountAgents = camelcaseKeys(rawAgentList, { deep: true });
          this.agents = accountAgents.map(agent => {
            const counts =
              this.assignmentCounts[String(agent.id)] || emptyAssignmentCount();
            return {
              ...agent,
              id: agent.id,
              name: agent.name,
              email: agent.email,
              avatarUrl: agentAvatarUrl(agent),
              activeAssignments: (counts.pending || 0) + (counts.onHold || 0),
            };
          });
        },
        { silent: true }
      );
    },
    syncAgentAvatar(agent) {
      const id = Number(agent?.id);
      if (!id) return;
      const avatarUrl = agentAvatarUrl(agent);
      const patchAgent = current =>
        Number(current?.id) === id ? { ...current, avatarUrl } : current;
      const patchOrder = order => {
        if (Number(order?.agent?.id) !== id) return order;
        return { ...order, agent: { ...order.agent, avatarUrl } };
      };
      this.agents = (this.agents || []).map(item => ({
        ...patchAgent(item),
        activeAssignments: item.activeAssignments,
      }));
      this.orders = this.orders.map(patchOrder);
      this.assignments = this.assignments.map(patchOrder);
      this.agentAssignedOrders = this.agentAssignedOrders.map(patchOrder);
      if (this.currentOrder) {
        this.currentOrder = patchOrder(this.currentOrder);
      }
    },
    async assignOrder(orderId, agentId) {
      return this.request('assignmentMutation', async () => {
        const record = extractRecord(
          await webOrdersAPI.assign(orderId, agentId)
        );
        if (!record?.id) {
          throw new Error('Assign response missing id');
        }
        upsert(this.orders, record);
        if (Number(this.currentOrder?.id) === Number(orderId)) {
          this.currentOrder = { ...this.currentOrder, ...record };
        }
        return record;
      });
    },
    async bulkAssignOrders(orderIds, agentId) {
      return this.request('assignmentMutation', async () => {
        const ids = (orderIds || []).map(id => Number(id)).filter(Boolean);
        if (!ids.length || !agentId) return [];
        const results = await Promise.all(
          ids.map(async id => {
            const record = extractRecord(
              await webOrdersAPI.assign(id, agentId)
            );
            if (!record?.id) {
              throw new Error('Assign response missing id');
            }
            return record;
          })
        );
        results.forEach(record => upsert(this.orders, record));
        if (
          this.currentOrder?.id &&
          ids.includes(Number(this.currentOrder.id))
        ) {
          const updated = results.find(
            row => Number(row?.id) === Number(this.currentOrder.id)
          );
          if (updated) this.currentOrder = { ...this.currentOrder, ...updated };
        }
        return results;
      });
    },
    async unassignOrder(orderId) {
      return this.request('assignmentMutation', async () => {
        const record = extractRecord(await webOrdersAPI.unassign(orderId));
        if (!record?.id) {
          throw new Error('Unassign response missing id');
        }
        upsert(this.orders, record);
        if (Number(this.currentOrder?.id) === Number(orderId)) {
          this.currentOrder = { ...this.currentOrder, ...record };
        }
        return record;
      });
    },
    async recordCall(orderId) {
      return this.request('callMutation', async () => {
        const response = await webOrdersAPI.recordCall(orderId);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Record call response missing id');
        }
        upsert(this.orders, record);
        if (Number(this.currentOrder?.id) === Number(orderId)) {
          this.currentOrder = { ...this.currentOrder, ...record };
        }
        return record;
      });
    },
    async addOrderNote(orderId, text) {
      return this.request('orderMutation', async () => {
        const response = await webOrdersAPI.addNote(orderId, text);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Order note response missing id');
        }
        upsert(this.orders, record);
        if (Number(this.currentOrder?.id) === Number(orderId)) {
          this.currentOrder = { ...this.currentOrder, ...record };
        }
        return record;
      });
    },
    async setOrderRecall(orderId, recalled) {
      return this.request('orderMutation', async () => {
        const response = await webOrdersAPI.recall(orderId, recalled);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Order recall response missing id');
        }
        upsert(this.orders, record);
        if (Number(this.currentOrder?.id) === Number(orderId)) {
          this.currentOrder = { ...this.currentOrder, ...record };
        }
        return record;
      });
    },
    async receiveReturn(orderId, payload = {}) {
      return this.request('orderMutation', async () => {
        const body = {};
        if (payload.returnWarehouseId) {
          body.return_warehouse_id = payload.returnWarehouseId;
        }
        const response = await webOrdersAPI.receive(orderId, body);
        const record = extractRecord(response);
        if (!record?.id) {
          throw new Error('Receive response missing id');
        }
        upsert(this.orders, record);
        if (Number(this.currentOrder?.id) === Number(orderId)) {
          this.currentOrder = { ...this.currentOrder, ...record };
        }
        return record;
      });
    },
    async fetchAgentPerformance(params = {}) {
      return this.request(
        'agentPerformance',
        async () => {
          const { data } = await webOrdersAPI.agentPerformance(params);
          const payload = data?.payload || {};
          this.agentPerformance = {
            from: payload.from,
            to: payload.to,
            agents: payload.agents || {},
          };
        },
        { silent: true }
      );
    },
    async fetchAgentOverviewAnalytics(params = {}) {
      return this.request(
        'overviewAnalytics',
        async () => {
          const { data } = await webOrdersAPI.overviewAnalytics(params);
          this.agentOverviewAnalytics = data?.payload || {};
        },
        { silent: true }
      );
    },
    async fetchAgentAssignedOrders(agentId, options = {}) {
      const {
        page = 1,
        perPage = 20,
        dailyDate,
      } = typeof options === 'string' || options instanceof Date
        ? { dailyDate: options }
        : options || {};

      const queueDate = String(dailyDate || '')
        .slice(0, 10)
        .trim();

      return this.request('agentAssignedOrders', async () => {
        if (!agentId) {
          this.agentAssignedOrders = [];
          this.agentAssignedOrdersMeta = {};
          this.agentAssignedAllMeta = {};
          return;
        }

        // Page 1 = today’s sheet (new assignments on top, leftover pending below).
        // Page 2+ = previous days’ history (leftover pending stays on page 1).
        const queueDateParam = queueDate || undefined;
        const uiPage = Number(page) || 1;

        if (uiPage <= 1) {
          const dailyParams = {
            filter_assigned_to: agentId,
            filter_agent_daily_queue: true,
            filter_daily_queue_date: queueDateParam,
            sort: 'assigned_at',
            direction: 'asc',
            per_page: 300,
            page: 1,
          };
          const historyMetaParams = {
            filter_assigned_to: agentId,
            filter_agent_assignment_history: true,
            filter_daily_queue_date: queueDateParam,
            sort: 'assigned_at',
            direction: 'desc',
            per_page: 1,
            page: 1,
          };
          const [dailyResponse, historyMetaResponse] = await Promise.all([
            webOrdersAPI.filter(dailyParams),
            webOrdersAPI.filter(historyMetaParams),
          ]);
          const daily = unwrap(dailyResponse);
          this.agentAssignedOrders = Array.isArray(daily.records)
            ? daily.records
            : [];
          this.agentAssignedOrdersMeta = daily.meta || {};
          this.agentAssignedAllMeta = unwrap(historyMetaResponse).meta || {};
          return;
        }

        const historyResponse = await webOrdersAPI.filter({
          filter_assigned_to: agentId,
          filter_agent_assignment_history: true,
          filter_daily_queue_date: queueDateParam,
          sort: 'assigned_at',
          direction: 'desc',
          per_page: perPage,
          page: uiPage - 1,
        });
        const { records, meta } = unwrap(historyResponse);
        this.agentAssignedOrders = Array.isArray(records) ? records : [];
        this.agentAssignedOrdersMeta = meta || {};
        this.agentAssignedAllMeta = meta || {};
      });
    },
    async fetchIntegrations() {
      return this.request(
        'integrations',
        async () => {
          const [storesResult, partnersResult] = await Promise.allSettled([
            woocommerceStoresAPI.list(),
            deliveryPartnerAccountsAPI.list(),
          ]);
          if (storesResult.status === 'fulfilled') {
            const storeRecords = unwrap(storesResult.value).records;
            this.stores = Array.isArray(storeRecords) ? storeRecords : [];
          }
          if (partnersResult.status === 'fulfilled') {
            const partnerRecords = unwrap(partnersResult.value).records;
            this.deliveryPartners = Array.isArray(partnerRecords)
              ? partnerRecords
              : [];
          }
          if (
            storesResult.status === 'rejected' &&
            partnersResult.status === 'rejected'
          ) {
            throw storesResult.reason;
          }
        },
        { silent: true }
      );
    },
    async fetchWebOrderSetting() {
      return this.request('webOrderSetting', async () => {
        const record = extractRecord(await webOrderSettingsAPI.get());
        this.webOrderSetting = record;
        return record;
      });
    },
    async updateWebOrderSetting(attributes) {
      return this.request('webOrderSettingMutation', async () => {
        const record = extractRecord(
          await webOrderSettingsAPI.update({
            web_order_setting: snakecaseKeys(attributes, { deep: true }),
          })
        );
        this.webOrderSetting = record;
        return record;
      });
    },
    async fetchDeliveryPartners() {
      return this.request(
        'integrations',
        async () => {
          const partners = await deliveryPartnerAccountsAPI.list();
          this.deliveryPartners = unwrap(partners).records;
        },
        { silent: true }
      );
    },
    async fetchPickupAddresses() {
      return this.request(
        'pickupAddresses',
        async () => {
          const { records } = unwrap(await webOrderPickupAddressesAPI.list());
          this.pickupAddresses = Array.isArray(records) ? records : [];
        },
        { silent: true }
      );
    },
    async createPickupAddress(attributes) {
      return this.request('pickupAddressMutation', async () => {
        const record = extractRecord(
          await webOrderPickupAddressesAPI.create({
            web_order_pickup_address: snakecaseKeys(attributes, { deep: true }),
          })
        );
        if (!record?.id) {
          throw new Error('Pickup address response missing id');
        }
        upsert(this.pickupAddresses, record);
        return record;
      });
    },
    async deletePickupAddress(id) {
      return this.request('pickupAddressMutation', async () => {
        await webOrderPickupAddressesAPI.delete(id);
        this.pickupAddresses = this.pickupAddresses.filter(
          item => Number(item.id) !== Number(id)
        );
      });
    },
    async fetchCourierDefaultImpact() {
      const response = await deliveryPartnerAccountsAPI.defaultImpact();
      const body = response?.data?.payload || response?.data || {};
      return camelcaseKeys(body, { deep: true });
    },
    async saveIntegration(type, record, options = {}) {
      const api =
        type === 'store' ? woocommerceStoresAPI : deliveryPartnerAccountsAPI;
      const root =
        type === 'store' ? 'woocommerce_store' : 'delivery_partner_account';
      return this.request('integrationMutation', async () => {
        const payload = { [root]: snakecaseKeys(record, { deep: true }) };
        if (options.reassignPending) {
          payload.reassign_pending = true;
        }
        if (record.id) await api.update(record.id, payload);
        else await api.create(payload);
        await this.fetchIntegrations();
      });
    },
    async syncStore(id) {
      return this.request('integrationMutation', async () => {
        await woocommerceStoresAPI.sync(id);
      });
    },
    async deleteIntegration(type, id) {
      const api =
        type === 'store' ? woocommerceStoresAPI : deliveryPartnerAccountsAPI;
      return this.request('integrationMutation', async () => {
        const response = await api.delete(id);
        await this.fetchIntegrations();
        return response;
      });
    },
    async createShipment(webOrderId, deliveryPartnerAccountId) {
      return this.request('shipmentMutation', async () => {
        await deliveryShipmentsAPI.create({
          delivery_shipment: {
            web_order_id: webOrderId,
            delivery_partner_account_id: deliveryPartnerAccountId,
          },
        });
        const record = extractRecord(await webOrdersAPI.show(webOrderId));
        if (!record?.id) {
          throw new Error('Order refresh after shipment missing id');
        }
        upsert(this.orders, record);
        if (Number(this.currentOrder?.id) === Number(webOrderId)) {
          this.currentOrder = record;
        }
        return record;
      });
    },
    applyRealtime(event, payload = {}) {
      const data = camelcaseKeys(payload || {}, { deep: true });
      if (event.startsWith('web_order.')) {
        if (event.endsWith('.deleted')) {
          const existing = findOrderSnapshot(this, data.id);
          if (existing?.status) this.adjustStatusCounts(existing.status, null);
          dropOrderFromList(this, data.id);
          if (Number(this.currentOrder?.id) === Number(data.id)) {
            this.currentOrder = null;
          }
        } else if (event.endsWith('.created')) {
          const existing = findOrderSnapshot(this, data.id);
          if (existing) {
            mergeOrderCollections(this, data);
          } else {
            if (data.status) this.adjustStatusCounts(null, data.status);
            if (matchesLiveNewChip(this, data)) this.liveNewOrderCount += 1;
          }
        } else {
          const existing = findOrderSnapshot(this, data.id);
          const previousStatus = existing?.status;
          mergeOrderCollections(this, data);
          const tab = this.ordersListStatus;
          if (existing && tab && data.status && data.status !== tab) {
            dropOrderFromList(this, data.id);
          }
          if (previousStatus && data.status && previousStatus !== data.status) {
            this.adjustStatusCounts(previousStatus, data.status);
          }
        }
      }
      if (event.startsWith('web_product.')) {
        if (event.endsWith('.deleted')) {
          this.products = this.products.filter(
            item => Number(item.id) !== Number(data.id)
          );
          if (Number(this.currentProduct?.id) === Number(data.id)) {
            this.currentProduct = null;
          }
        } else {
          upsert(this.products, data);
          if (Number(this.currentProduct?.id) === Number(data.id)) {
            this.currentProduct = { ...this.currentProduct, ...data };
          }
        }
      }
      if (event === 'web_order.assignment_updated') this.fetchAssignments();
      if (event.startsWith('delivery_shipment.')) {
        const orderPayload = data.webOrder;
        if (orderPayload) {
          const existing =
            this.orders.find(
              item => Number(item.id) === Number(orderPayload.id)
            ) ||
            (Number(this.currentOrder?.id) === Number(orderPayload.id)
              ? this.currentOrder
              : null);
          const shipment = { ...data };
          delete shipment.webOrder;
          const deliveryShipments = [
            ...(existing?.deliveryShipments || []).filter(
              item => Number(item.id) !== Number(shipment.id)
            ),
            shipment,
          ];
          mergeOrderCollections(this, { ...orderPayload, deliveryShipments });
        }
      }
      if (event.startsWith('woocommerce_store.')) {
        if (event.endsWith('.deleted')) {
          this.stores = this.stores.filter(
            item => Number(item.id) !== Number(data.id)
          );
        } else {
          upsert(this.stores, data);
        }
      }
      if (event.startsWith('delivery_partner_account.')) {
        if (event.endsWith('.deleted')) {
          this.deliveryPartners = this.deliveryPartners.filter(
            item => Number(item.id) !== Number(data.id)
          );
        } else {
          upsert(this.deliveryPartners, data);
        }
      }
    },
  },
});

export { unwrap, extractRecord, formatDateRangeLabel };
